import { StreamProvider } from "@teamrota/shiftstreamlib";
import { colors } from "@teamrota/rota-design";

import { SHIFT_TYPES } from "~/src/consts";

/**
 * Return several basic props + styles to be used in all implementations of shift card
 */
export const getCardProps = (
  shiftOrShifts,
  hasCancelledBookings = false,
  isCancelled = undefined,
  hasReservedBookings = false,
  isGridCard = false
) => {
  const isArray = Array.isArray(shiftOrShifts);
  const isMultipleShifts = isArray && shiftOrShifts.length > 1;

  const shift = isArray
    ? shiftOrShifts.length === 1
      ? shiftOrShifts[0]
      : null
    : shiftOrShifts;

  const isNightShift = shift?.type === SHIFT_TYPES.NIGHT;
  const isDraft = shift?.isDraft;

  const isPredicted = shift?.isPredicted;
  const isInternal =
    shift?.stream?.account?.source === StreamProvider.INTERNAL;
  const isEmail = shift?.stream?.account?.source === StreamProvider.EMAIL;
  const isStream = !!shift?.stream && !isEmail && !isInternal;

  const isRebook = shift?.isShiftRebook;
  const isOnCallTimesEnabled = shift?.isOnCallTimesEnabled;

  if (isCancelled === undefined) {
    isCancelled = !!shift?.cancelledAt;
  }

  const color = isNightShift && !isDraft ? "white" : "black";

  const borderColor = isCancelled
    ? "#ff0000"
    : hasCancelledBookings
    ? "#eebb00"
    : hasReservedBookings
    ? "#999999"
    : isInternal
    ? "#842ff7"
    : isStream || isPredicted
    ? "#84f72f"
    : isEmail
    ? "#2f84f7"
    : "#ff9300";

  const border = isDraft
    ? `2px dashed ${borderColor}`
    : `2px solid ${borderColor}`;

  const backgroundColor = isDraft
    ? "white"
    : hasReservedBookings && isGridCard
    ? isNightShift
      ? "#666"
      : "#d3d3d3"
    : hasReservedBookings && !isGridCard
    ? colors.lighterOrange
    : isInternal && isNightShift
    ? "#2d0563"
    : isInternal
    ? "#CAA3FF"
    : (isStream || isPredicted) && isNightShift
    ? "#2d6305"
    : isStream || isPredicted
    ? "#CAFFA3"
    : isEmail && isNightShift
    ? "#052d63"
    : isEmail
    ? "#A3CAFF"
    : isRebook && isNightShift
    ? "#4e0452"
    : isRebook
    ? "#f28fce"
    : isNightShift
    ? "#313131"
    : colors.lightOrange;

  const backgroundCancelledColor = isNightShift ? "#6b0a0acc" : "#ffcccc";
  const backgroundReservedColor = isEmail
    ? isNightShift
      ? "#052d63cc"
      : "#A3CAFF"
    : isInternal
    ? isNightShift
      ? "#2d0563cc"
      : "#CAA3FF"
    : isNightShift
    ? "#666666cc"
    : "#cccccc";

  const backgroundReservedColor2 = isEmail
    ? isNightShift
      ? "#333"
      : "#ddd"
    : isInternal
    ? isNightShift
      ? "#333"
      : "#ddd"
    : isNightShift
    ? "#333"
    : "#ddd";

  const background = isPredicted
    ? `linear-gradient(${backgroundColor} 2px, transparent 2px)  -2px -2px / 50px 50px, ` +
      `linear-gradient(90deg, ${backgroundColor} 2px, transparent 2px)  -2px -2px / 50px 50px, ` +
      `linear-gradient(${backgroundColor} 1px, transparent 1px)  -1px -1px / 10px 10px, ` +
      `linear-gradient(90deg, ${backgroundColor} 1px, ${backgroundColor}88 1px)  -1px -1px / 10px 10px`
    : isCancelled
    ? `repeating-linear-gradient(-60deg,${backgroundColor}70 0px 10px,${backgroundCancelledColor} 10px 20px)`
    : hasCancelledBookings
    ? `repeating-linear-gradient(-60deg,${backgroundColor} 0px 10px,${backgroundColor}BB 10px 20px)`
    : hasReservedBookings && !isGridCard
    ? `repeating-linear-gradient(-60deg,${backgroundReservedColor2} 0px 10px,${backgroundReservedColor} 10px 20px)`
    : backgroundColor;

  let tooltip = undefined;

  if (isMultipleShifts) {
    tooltip = `IDs #${shiftOrShifts
      ?.map?.(({ id, identfier }) => identfier | id)
      .join(", #")}`;
  } else if (shift) {
    tooltip = `ID #${shift.identfier || shift.id} - a `;
    if (isCancelled) tooltip += "cancelled ";
    if (isPredicted) tooltip += "predicted ";
    if (isRebook) tooltip += "rebooked ";
    if (isOnCallTimesEnabled) tooltip + "on call ";
    tooltip += isNightShift ? " night " : "day ";
    tooltip += "shift ";

    if (isEmail) {
      tooltip += `via Email2Shift`;
    } else if (isInternal) {
      tooltip += "via ShiftStream Internal";
    } else if (isStream) {
      tooltip += `via ShiftStream (${shift?.stream?.account?.sourceName})`;
    }
  }

  return {
    title: tooltip,
    color,
    border,
    backgroundColor,
    background
  };
};
